/**
 * Hooks TanStack Query — POD pricing config + catalogue V4
 * (bascule Gelato 2026-05-17).
 *
 * Les hooks cascade (`usePodPapers`/`Frames`/`Mounts`/`Glazes`) ont été
 * retirés en V4 : le wizard `<PrintableSpecModal>` consomme désormais
 * `useActivePodCatalogSkus` (1 fetch du cache, filtre client-side par
 * `provider_display_name`) pour l'autocomplete productUid.
 *
 * Les hooks pricing config restent (lecture + édition de la marge atelier
 * + frais Etsy + tiers profit).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  getPodPricingConfig,
  listPodCatalogSkus,
  updatePodPricingConfig,
  type PodSkuCatalogPaginated,
} from '@/utils/api/pod-catalog'
import type { components } from '@/types/api-generated'

type PodPricingConfigUpdate = components['schemas']['PodPricingConfigUpdate']

// Cap à 200 (page_size max côté BE). En pratique le cache Gelato actif
// tient confortablement sous cette limite — si on dépasse, l'autocomplete
// ratera les SKUs en queue ; on ré-évaluera (pagination ou query param
// `q` côté BE) quand le cas se présentera.
const POD_CATALOG_PAGE_SIZE = 200

export function useActivePodCatalogSkus(category?: string) {
  return useQuery<PodSkuCatalogPaginated>({
    queryKey: ['pod-catalog', 'active', category ?? null],
    queryFn: () =>
      listPodCatalogSkus({
        status: 'active',
        category,
        page: 1,
        page_size: POD_CATALOG_PAGE_SIZE,
      }),
    staleTime: 60_000,
  })
}

export function usePodPricingConfig() {
  return useQuery({
    queryKey: ['pod-pricing-config'],
    queryFn: () => getPodPricingConfig(),
    staleTime: 60_000,
  })
}

export function useUpdatePodPricingConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: PodPricingConfigUpdate) => updatePodPricingConfig(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pod-pricing-config'] })
    },
  })
}
